import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Click-and-drag panning for a horizontally scrollable element.
 *
 * A wide table is reachable by trackpad or by hunting for the scrollbar at the very bottom of the
 * page; neither is much use when the interesting columns are off to the right and the scrollbar is
 * a hundred rows away. Grabbing the table and pulling it is the obvious gesture, so this wires it up.
 *
 * Three things it deliberately gets out of the way of:
 *  - **Clicks.** Nothing happens until the pointer has travelled `THRESHOLD` px, so a plain click
 *    still opens the row. Once it IS a drag, the click that ends it is swallowed in the capture
 *    phase — otherwise letting go over a row would navigate to whatever you dragged onto.
 *  - **Controls.** A drag starting on a button, link, input or menu is ignored, so row actions,
 *    sort headers and the column picker keep working normally.
 *  - **Text selection.** Suppressed for the duration of a drag only, so selecting a cell's text by
 *    click-dragging inside it still works right up until it becomes a pan.
 *
 * Returns a ref for the scroll container and `pannable` — true only while the content actually
 * overflows, so the grab cursor never appears on a table that has nothing to pan.
 */
const THRESHOLD = 5;

export function useDragScroll<T extends HTMLElement = HTMLDivElement>() {
  const ref = useRef<T | null>(null);
  const [pannable, setPannable] = useState(false);

  // Recompute on resize AND on content change — revealing a column is what makes a table pannable.
  const measure = useCallback(() => {
    const el = ref.current;
    if (el) setPannable(el.scrollWidth > el.clientWidth + 1);
  }, []);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    const mo = new MutationObserver(measure);
    mo.observe(el, { childList: true, subtree: true, attributes: true, attributeFilter: ['style', 'class'] });
    window.addEventListener('resize', measure);
    return () => { ro.disconnect(); mo.disconnect(); window.removeEventListener('resize', measure); };
  }, [measure]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let active = false;      // pointer is down on a draggable area
    let panning = false;     // movement passed the threshold — this is a real drag
    let startX = 0;
    let startLeft = 0;
    let pointerId = -1;
    let swallowClick = false;

    // NOTE: this list must never contain a CONTAINER role. `[role="dialog"]` used to be here, and
    // since closest() walks UP, every cell of every table inside a modal matched it — so grab-and-drag
    // panning was dead in all of them (a wallet ledger, for one, whose scrollbar sits below the fold).
    // It guarded nothing either way: menus and dialogs are rendered through a portal, i.e. outside this
    // scroll container, so their pointer events never reach this listener to begin with.
    const interactive = (t: EventTarget | null) =>
      t instanceof Element &&
      t.closest('button, a, input, select, textarea, label, [role="menu"], [role="menuitem"], [contenteditable="true"]');

    const onPointerDown = (e: PointerEvent) => {
      // Left button only, and only when there is something to pan. Middle/right keep native behaviour.
      if (e.button !== 0 || el.scrollWidth <= el.clientWidth || interactive(e.target)) return;
      // A press on the container's OWN scrollbar gutter stays native. The browser is already dragging
      // the thumb, and panning too would move the table twice over — in the opposite direction, since
      // dragging a thumb right scrolls right while pulling content right scrolls left.
      if (e.target === el && (e.offsetX > el.clientWidth || e.offsetY > el.clientHeight)) return;
      active = true;
      panning = false;
      startX = e.clientX;
      startLeft = el.scrollLeft;
      pointerId = e.pointerId;
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!active) return;
      const dx = e.clientX - startX;
      if (!panning) {
        if (Math.abs(dx) < THRESHOLD) return;
        panning = true;
        try { el.setPointerCapture(pointerId); } catch { /* capture is best-effort */ }
        el.dataset.panning = 'true';
      }
      el.scrollLeft = startLeft - dx;
      e.preventDefault(); // stops the browser starting a text selection mid-pan
    };

    const end = () => {
      if (!active) return;
      active = false;
      if (panning) {
        panning = false;
        swallowClick = true;
        delete el.dataset.panning;
        try { el.releasePointerCapture(pointerId); } catch { /* already released */ }
      }
    };

    // Capture phase: the click fired by the pointer-up that ENDED a drag must not reach the row.
    const onClickCapture = (e: MouseEvent) => {
      if (!swallowClick) return;
      swallowClick = false;
      e.stopPropagation();
      e.preventDefault();
    };

    el.addEventListener('pointerdown', onPointerDown);
    el.addEventListener('pointermove', onPointerMove);
    el.addEventListener('pointerup', end);
    el.addEventListener('pointercancel', end);
    el.addEventListener('click', onClickCapture, true);
    return () => {
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerup', end);
      el.removeEventListener('pointercancel', end);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  return { ref, pannable };
}
